import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, BookMarked, BarChart3, LogOut, TrendingUp, Bell, Database, Zap, User, Search, GitCompare, Shield, Calendar, Building2, CreditCard, HelpCircle, Users, BarChart2, Activity, MessageSquare, Settings, Menu, X, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ProTrackMark, StackLogo } from "./ProTrackLogo";
import { usePlanAccess } from "./PlanGate";
import toast from "react-hot-toast";

const SUBSCRIBER_NAV = [
  { label: "INTELLIGENCE", items: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/tenders", icon: FileText, label: "Tenders" },
    { to: "/submit-tender", icon: Send, label: "Submit a Tender" },
    { to: "/matched", icon: Zap, label: "AI Matched", highlight: true },
    { to: "/search", icon: Search, label: "Advanced Search" },
    { to: "/compare", icon: GitCompare, label: "Compare" },
    { to: "/saved", icon: BookMarked, label: "Saved" },
    { to: "/analytics", icon: BarChart3, label: "Analytics" },
  ]},
  { label: "COMPLIANCE", items: [
    { to: "/compliance", icon: Shield, label: "Compliance Hub", gate: "compliance_hub" },
    { to: "/hse-report", icon: Shield, label: "HSE Reports", gate: "hse_reports" },
    { to: "/regulatory-calendar", icon: Calendar, label: "Reg. Calendar", gate: "regulatory_calendar" },
    { to: "/market-intelligence", icon: TrendingUp, label: "Market Intel", gate: "market_intel" },
  ]},
  { label: "ACCOUNT", items: [
    { to: "/companies", icon: Building2, label: "Companies" },
    { to: "/billing", icon: CreditCard, label: "Billing & Plans" },
    { to: "/alerts", icon: Bell, label: "Alerts" },
    { to: "/scrapers", icon: Database, label: "Data Sources" },
    { to: "/profile", icon: User, label: "Profile" },
    { to: "/support", icon: HelpCircle, label: "Support" },
  ]},
];

const ADMIN_NAV = [
  { label: "OVERVIEW", items: [
    { to: "/admin", icon: BarChart2, label: "Admin Dashboard" },
  ]},
  { label: "MANAGEMENT", items: [
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/subscriptions", icon: CreditCard, label: "Subscriptions" },
    { to: "/admin/revenue", icon: TrendingUp, label: "Revenue" },
  ]},
  { label: "PLATFORM", items: [
    { to: "/admin/sources", icon: Database, label: "Data Sources" },
    { to: "/admin/health", icon: Activity, label: "System Health" },
    { to: "/admin/broadcast", icon: MessageSquare, label: "Broadcast" },
    { to: "/admin/support", icon: HelpCircle, label: "Support Inbox" },
  ]},
  { label: "ACCOUNT", items: [
    { to: "/profile", icon: User, label: "Profile" },
  ]},
];

function NavItem({ to, icon: Icon, label: itemLabel, highlight, gate, onNavigate }) {
  // Hook must be called unconditionally — Rules of Hooks
  const gateResult = usePlanAccess(gate || "tenders_browse");
  const gateAccess = gate ? gateResult : true;
  return (
    <NavLink
      to={to}
      onClick={gateAccess ? onNavigate : (e) => { e.preventDefault(); onNavigate(); }}
      style={({ isActive }) => ({
        display: "flex", alignItems: "center", gap: "10px",
        padding: "8px 10px", borderRadius: "9px", textDecoration: "none",
        fontSize: "13px", fontWeight: "500", transition: "all 0.15s",
        background: isActive && gateAccess ? "var(--accent-green-dim)" : "transparent",
        color: !gateAccess ? "var(--text-muted)" : isActive ? "var(--accent-green)" : highlight ? "var(--accent-green)" : "var(--text-secondary)",
        border: isActive && gateAccess ? "1px solid rgba(0,212,161,0.2)" : "1px solid transparent",
        opacity: gateAccess ? 1 : 0.55,
      })}
    >
      <Icon size={15} />
      {itemLabel}
      {highlight && gateAccess && (
        <span style={{ marginLeft: "auto", fontSize: "9px", background: "var(--accent-green)", color: "#0a0f1e", padding: "2px 5px", borderRadius: "100px", fontWeight: "700" }}>AI</span>
      )}
      {!gateAccess && (
        <span style={{ marginLeft: "auto", fontSize: "9px", color: "var(--text-muted)" }}>🔒</span>
      )}
    </NavLink>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";
  const navSections = isAdmin ? ADMIN_NAV : SUBSCRIBER_NAV;

  // Close on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/login");
  };

  return (
    <>
      <style>{`
        .sidebar-hamburger {
          display: none;
          position: fixed;
          top: 14px;
          left: 14px;
          z-index: 200;
          width: 40px;
          height: 40px;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 10px;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          color: var(--text-primary);
          transition: background 0.2s;
        }
        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.65);
          z-index: 150;
          backdrop-filter: blur(2px);
        }
        .sidebar-drawer {
          transform: translateX(0);
          transition: transform 0.25s ease;
        }
        .sidebar-close-btn { display: none; }
        .sidebar-mobile-logout {
          display: none;
          position: fixed;
          top: 14px;
          left: 64px;
          z-index: 200;
          width: 40px;
          height: 40px;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 10px;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          transition: all 0.2s;
        }
        .sidebar-mobile-logout:hover { color: var(--accent-red); background: var(--accent-red-dim); }
        @media (max-width: 768px) {
          .sidebar-hamburger { display: flex; }
          .sidebar-mobile-logout { display: flex; }
          .sidebar-logo-area { padding-top: 52px !important; }
          .sidebar-overlay { display: block; opacity: 0; pointer-events: none; transition: opacity 0.25s; }
          .sidebar-overlay.open { opacity: 1; pointer-events: auto; }
          .sidebar-drawer { transform: translateX(-100%); }
          .sidebar-drawer.open { transform: translateX(0); }
          .sidebar-close-btn { display: flex !important; }
        }
      `}</style>

      {/* Mobile top bar — hamburger + quick logout */}
      <button className="sidebar-hamburger" onClick={() => setMobileOpen(true)} aria-label="Open menu">
        <Menu size={20} />
      </button>
      <button
        className="sidebar-mobile-logout"
        onClick={handleLogout}
        aria-label="Sign out"
        title="Sign out"
      >
        <LogOut size={16} />
      </button>

      {/* Overlay */}
      <div className={`sidebar-overlay${mobileOpen ? " open" : ""}`} onClick={() => setMobileOpen(false)} />

      {/* Drawer */}
      <div className={`sidebar-drawer${mobileOpen ? " open" : ""}`} style={{
        width: "260px", height: "100vh",
        background: "var(--bg-secondary)",
        borderRight: "1px solid var(--border)",
        position: "fixed", left: 0, top: 0,
        display: "flex", flexDirection: "column",
        padding: "18px 14px",
        zIndex: 160,
        overflowY: "auto",
      }}>

        {/* Mobile close button */}
        <button
          className="sidebar-close-btn"
          onClick={() => setMobileOpen(false)}
          style={{ position: "absolute", top: "14px", right: "14px", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--text-secondary)", width: "32px", height: "32px", cursor: "pointer", alignItems: "center", justifyContent: "center" }}
        >
          <X size={16} />
        </button>

        {/* Logo — extra top padding on mobile so hamburger buttons don't overlap */}
        <div className="sidebar-logo-area" style={{ padding: "0 6px", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <ProTrackMark size={30} />
            <div>
              <div style={{ fontSize: "16px", fontWeight: "800", letterSpacing: "-0.3px", color: "var(--text-primary)" }}>
                ProTrack<span style={{ color: "var(--accent-green)" }}>NG</span>
              </div>
              <div style={{ fontSize: "10px", color: isAdmin ? "var(--accent-orange)" : "var(--text-muted)", letterSpacing: "0.3px", fontWeight: isAdmin ? "700" : "400" }}>
                {isAdmin ? "Admin Console" : "Tender Intelligence"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px", paddingLeft: "2px" }}>
            <span style={{ fontSize: "9px", color: "var(--text-muted)", opacity: 0.6 }}>powered by</span>
            <StackLogo size={13} variant="white" />
            <span style={{ fontSize: "9px", color: "var(--text-muted)", fontWeight: "600", opacity: 0.6 }}>ProStack NG</span>
          </div>
        </div>

        {/* Admin badge */}
        {isAdmin && (
          <div style={{ margin: "0 4px 16px", padding: "8px 12px", background: "rgba(255,140,66,0.08)", border: "1px solid rgba(255,140,66,0.2)", borderRadius: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Settings size={13} color="var(--accent-orange)" />
            <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--accent-orange)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Super Admin</span>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: "18px" }}>
          {navSections.map(({ label, items }) => (
            <div key={label}>
              <div style={{ fontSize: "10px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.08em", padding: "0 8px", marginBottom: "5px" }}>
                {label}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                {items.map((item) => <NavItem key={item.to} {...item} onNavigate={() => setMobileOpen(false)} />)}
              </div>
            </div>
          ))}
        </nav>

        {/* User + Logout */}
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", background: "var(--bg-card)", borderRadius: "10px", border: "1px solid var(--border)" }}>
            <div style={{ width: "28px", height: "28px", background: isAdmin ? "var(--accent-orange)" : "var(--accent-green)", borderRadius: "7px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", color: "#0a0f1e", flexShrink: 0 }}>
              {user?.full_name?.charAt(0) || "U"}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user?.full_name}
              </div>
              <div style={{ fontSize: "10px", color: isAdmin ? "var(--accent-orange)" : "var(--text-muted)", textTransform: "capitalize", fontWeight: isAdmin ? "700" : "400" }}>
                {user?.role?.replace("_", " ")}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", borderRadius: "9px", fontSize: "13px", width: "100%", transition: "all 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.color = "var(--accent-red)"; e.currentTarget.style.background = "var(--accent-red-dim)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.background = "none"; }}
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>
    </>
  );
}
